import React, {
  useContext, useEffect, useState, useRef,
} from 'react';
import {
  useParams, useHistory,
} from 'react-router-dom';
import { useFullScreenHandle } from 'react-full-screen';
import { useTheme } from 'styled-components';

// components
import { useReactToPrint } from 'react-to-print';
import GridKpis from '../../components/GridKpis';
import useGridKpi from '../../hooks/useGridKpi';
import GridKpisToolbar from '../../components/Toolbars/GridKpisToolbar';
import Mural from '../../components/Mural';
import ShareList from '../../juristec-ui/core/ShareList';
import CreateTemplate from '../../components/Modals/CreateTemplate';
import DashboardDependencies from '../../components/Modals/DashboardDependencies';
import NoKpi from '../../juristec-ui/kpis/grid/NoKpi';
import Loader from '../../juristec-ui/core/Loader';
import GlobalFiltersColumns from '../../components/Modals/GlobalFiltersColumns';

// hooks
import useDeviceType from '../../juristec-ui/hooks/useDeviceType';

// functions & others
import { TourContext } from '../../context/TourProvider';
import { AuthContext } from '../../context/AuthProvider';
import { UsersContext } from '../../context/UsersProvider';
import { ModalContext } from '../../context/ModalProvider';
import { AlertContext } from '../../context/AlertProvider';
import { FilesContext } from '../../context/FilesProvider';
// import { AlertContext, ModalContext, Loader } from '../../components/Juristec/core';
import useGridDashboard from '../../hooks/useGridDashboard';
import useKpiData from '../../hooks/useKpiData';
import urlHash from '../../utils/functions/urlHash';
import { shareDashOptions } from '../../options';
import { error } from '../../label';

import { verifyFileImgType, verifyFileSize } from '../../juristec-ui/utils/validators/fileValidators';

const printLoadMsg = 'Preparando a impressão. Esta tarefa pode levar alguns segundos...';

const getType = (typeLabel) => {
  switch (typeLabel) {
    case 'date': 
      return 'datetime64[ns]';
    case 'float':
      return 'float64';
    default:
      return 'category';
  }
};

export default function KpisPage() {
  const history = useHistory();
  const { hash } = useParams();
  const theme = useTheme();

  const getParams = () => {
    const str = urlHash(hash, true).split('/');
    return { dashboardKey: str[0], userId: str[1] };
  };
  const { dashboardKey, userId } = getParams();
  const {
    currentUser, userCompany, user, claimsUser,
  } = useContext(AuthContext);
  const { state: usersState } = useContext(UsersContext);
  const { state: filesState, filesAPI } = useContext(FilesContext);
  const { closeModal, setModalConfig } = useContext(ModalContext);
  const { setAlertConfig } = useContext(AlertContext);

  const gridKpiRef = useRef();
  const { tourOpen, nextStep } = useContext(TourContext);
  const handle = useFullScreenHandle();
  const onBeforeGetContentResolve = useRef();

  const [kpiDataAPI] = useKpiData(currentUser);

  const device = useDeviceType();

  const isOwner = userId === currentUser.uid;
  const dashboardKeyValid = dashboardKey.split('_')[1] || dashboardKey;
  // const dashboardKeyValid = isOwner ? dashboardKey : dashboardKey.split('_')[1];

  const [gridKpiState, gridKpiAPI] = useGridKpi(userId, dashboardKeyValid, currentUser, user);
  const [editPainel, setEditPainel] = useState(null);
  const [openMural, setOpenMural] = useState(false);
  const [gridBreakpoint, setGridBreakpoint] = useState('lg');
  const [showToolbar, setShowToolbar] = useState(true);

  const [showToPrint, setShowToPrint] = useState(false);
  const [manualLoad, setManualLoad] = useState(false);

  const [dashFiles, setDashFiles] = useState([]);
  const [globalFilterSelectors, setGlobalFilterSelectors] = useState({});
  const globalFilters = useRef({});
  // const dashLoaded = useRef(false);

  const {
    dashboardDoc,
    kpiItemList,
    kpisLayout,
    collection,
    isLoading,
  } = gridKpiState;

  useEffect(() => {
    gridKpiAPI.loadCardsFromCollection();
  }, [gridKpiAPI]);

  useEffect(() => {
    if (dashboardDoc && (!dashboardDoc.status || dashboardDoc.isDeleted)) {
      setAlertConfig({
        type: 'warning',
        text: 'Dashboard não encontrado!',
      });
      history.replace('/home');
    }
  }, [dashboardDoc, history]);

  useEffect(() => {
    if (dashboardDoc?.globalFilters && dashFiles.length > 0) {
      setGlobalFilterSelectors(dashboardDoc.globalFilters?.reduce((aux, gf) => {
        aux[`${gf.database}${gf.column}${gf.selector}`] = {
          ...gf,
          filename: dashFiles.find((ff) => ff.file_id === gf.database)?.filename || '',
          isOpen: false,
        };
        return aux;
      }, {}));
      globalFilters.current = {};
    }
  }, [dashboardDoc?.globalFilters, dashFiles.length]);

  useEffect(() => {
    if (!filesState.started && !filesState.fileLoading) {
      (async () => {
        await filesAPI.init();
      })();
    }
  }, [filesState.started, filesAPI, filesState.fileLoading]);

  useEffect(() => {
    if ((filesState?.files?.length > 0 || filesState?.instanceFiles?.length > 0) && dashboardDoc?.databases) {
      setDashFiles(filesState.instanceFiles.reduce((aux, fileInfo) => {
        if (dashboardDoc.databases.includes(fileInfo.file_id)) {
          const file = filesState.files.find((fullFile) => fullFile.file_id === fileInfo.file_id);
          if (file) {
            aux.push({
              ...file,
              label: file.filename,
              value: file.file_id,
              id: file.file_id,
              allowed: true,
            });
          } else {
            aux.push({
              ...fileInfo,
              allowed: false,
            });
          }
        }
        return aux;
      }, []));
    }
  }, [filesState.files, filesState.instanceFiles, dashboardDoc?.databases]);

  const closeMural = () => setOpenMural(false);

  const changeCurrentSnap = async (snapId) => {
    const result = await gridKpiAPI.loadCardsFromCollection(snapId);
    if (result.error) {
      setAlertConfig({
        type: 'error',
        text: result.msg,
        child: `${result.raw}`,
      });
    }
  };

  function snapshotReachedtLimit(snaps) {
    const limit = +userCompany.snapshots;
    if (!limit) throw new Error('Quantidade de snapshots não numerico');
    return snaps.length >= limit;
  }

  const handleCreateSnapshot = async (snapName) => {
    try {
      const oldsSnaps = dashboardDoc.snapshots || [];
      if (snapshotReachedtLimit(oldsSnaps)) {
        setAlertConfig({
          type: 'error',
          text: 'Limite de Stories alcançados!',
          child: 'Para incluir um novo story você deve apagar algum dos existentes para liberar espaço.',
        });
        return;
      }
      const result = await gridKpiAPI
        .saveSnapshot(snapName, oldsSnaps, kpiItemList, kpisLayout);
      // (snapName, oldSnaps, cardList, cardsLayout, deleteOlder);
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: `${result.raw}`,
        });
        return;
      }

      setAlertConfig({
        type: 'success',
        text: 'Story criado com sucesso!',
      });
      if (tourOpen) nextStep();
      closeModal();
    } catch (er) {
      setAlertConfig({
        type: 'error',
        text: 'Algo deu errado!',
        // child: `${result.raw}`,
      });
    }
  };

  // editar o nome do snapshot
  const handleEditSnapshot = async (name, snapId) => {
    const oldSnaps = dashboardDoc.snapshots;
    const result = await gridKpiAPI.editSnapshot({ name, snapId }, oldSnaps);
    if (result.error) {
      setAlertConfig({
        type: 'error',
        text: result.msg,
        child: `${result.raw}`,
      });
      return;
    }

    setAlertConfig({
      type: 'success',
      text: 'Story editado com sucesso!',
    });
    closeModal();
  };

  // deletar o nome do snapshot
  const handleDeleteSnapshot = (snap) => {
    const deleteSnapshot = async () => {
      const oldSnaps = dashboardDoc.snapshots;
      const result = await gridKpiAPI.removeSnapshot(snap, oldSnaps);
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: `${result.raw}`,
        });
        return;
      }

      setAlertConfig({
        type: 'success',
        text: 'Story apagado com sucesso!',
      });

      // se deletar no qual está selecionado, volta para o 'kpis'
      if (collection === snap.snapId) {
        await changeCurrentSnap();
      }
      closeModal();
    };

    setAlertConfig({
      type: 'warning',
      text: 'Tem certeza que deseja excluir o story?',
      child: (
        <span>
          O story
          {' '}
          <strong style={{ fontStyle: 'italic' }}>
            {snap.name}
          </strong>
          {' '}
          não poderá ser recuperado!
        </span>
      ),
      withoutConfirm: true, // tirar o ok do alerta
      withFunction: true, // colocar btn cancelar e confirmar
      confirmFunction: deleteSnapshot,
    });
  };

  const handleCardStyles = async (cardStyles) => {
    const result = await gridKpiAPI.setCardStyles(cardStyles);
    if (result.error) {
      setAlertConfig({
        type: 'error',
        text: result.msg,
        child: `${result.raw}`,
      });
      return;
    }

    setAlertConfig({
      type: 'success',
      text: 'Estilo editado com sucesso!',
    });
    closeModal();
  };

  const handleSaveLayout = async (newLayout) => {
    const result = await gridKpiAPI.saveLayout(newLayout);
    if (result.error) setAlertConfig({ type: 'error', text: 'Erro ao salvar o layout' });
  };

  const [{ isLoading: gridAPILoading }, gridAPI] = useGridDashboard(currentUser, user);

  const handleShare = () => {
    const genSuccessMsg = (dashName, sharedUsers = [], missing) => {
      let child = '';
      let type = 'success';
      if (sharedUsers.length > 0) {
        child = `O dashboard ${dashName} está compartilhado com ${sharedUsers.map((u) => (
          `${u.name} (${shareDashOptions.get(u.sharePermission?.value)})`)).join(', ')}.`;
        if (missing?.deleted?.length > 0 || missing?.noPermission?.length > 0) {
          type = 'warning';
          child += " Porém alguns KPI's dependem de";
          if (missing.deleted.length > 0) {
            child += ` ${missing.deleted.length} arquivos não encontrados${missing.noPermission.length > 0 ? ' e' : '.'}`;
          }
          if (missing.noPermission.length > 0) child += ` ${missing.deleted.length} arquivos que não são seus.`;
        }
      } else {
        child = `O dashboard ${dashName} não está compartilhado com outros usuários.`;
      }
      return {
        text: 'Opções de compartilhamento atualizadas!',
        type,
        child,
      };
    };

    const submitShare = async (selected, removed) => {
      const result = await gridAPI.shareDashboard(
        gridKpiState.dashboardDoc, usersState.users, selected, removed, filesState.files,
      );
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: `${result.raw}`,
        });
        return;
      }
      gridKpiAPI.loadCardsFromCollection();
      // await gridKpiAPI.setDashInfo(gridKpiState.dashboardDoc); // atualizar o doc //
      setAlertConfig(genSuccessMsg(gridKpiState.dashboardDoc.name, selected, result.missing));
      closeModal(); // aqui...
    };

    const genPublicUrl = async (revoke) => {
      const res = await gridKpiAPI.genPublicKpis(dashboardDoc, collection, revoke);
      if (res.error) {
        setAlertConfig({
          type: 'error',
          text: `Não foi possível ${revoke ? 'desativar o link público' : 'gerar cópia pública'}. Tente novamente mais tarde`,
        });
      } else {
        setAlertConfig({
          type: 'success',
          text: `Link público ${revoke ? 'desativado' : 'atualizado'} com sucesso.`,
        });
      }
      return res;
    };

    setModalConfig({
      title: 'Compartilhar Dashboard',
      close: closeModal,
      children: (
        <ShareList
          users={usersState.users}
          owner={gridKpiState?.dashboardDoc.owner}
          publicLink={userCompany.publicLink}
          sharedWith={gridKpiState?.dashboardDoc?.sharedWith}
          submit={submitShare}
          close={closeModal}
          publicUrl={dashboardDoc.publicUrl}
          publicTimestamp={dashboardDoc.publicTimestamp}
          genPublicUrl={genPublicUrl}
          permissionsOpts={shareDashOptions.getOptions()}
        />
      ),
    });
  };

  const handleCreateTemplate = async () => {
    const submitNewTemplate = async (template) => {
      const result = await gridKpiAPI.createTemplate(template, kpiItemList, kpisLayout);
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: `${result.raw}`,
        });
        return;
      }

      setAlertConfig({
        type: 'success',
        text: 'Template criado com sucesso!',
      });
      closeModal();
    };

    // Function to check if the image satisfies the specified conditions
    const checkFileIsImg = (file) => {
      const errorMsg = verifyFileImgType(file) || verifyFileSize(file, 1000000);
      if (errorMsg.length > 0) {
        setAlertConfig({ type: 'error', text: errorMsg });
        return false;
      }
      return true;
    };

    const columnsRequired = await gridKpiAPI.getColumnDependecies(kpiItemList, dashboardDoc?.globalFilters);
    // const aux = Object.keys(columnsRequired).reduce((acc, cur) => {
    //   acc[cur] = { type: columnsRequired[cur], description: '' };
    //   return acc;
    // }, {});

    setModalConfig({
      title: 'Criar Template',
      children: (
        <CreateTemplate
          hide={closeModal}
          submitData={submitNewTemplate}
          templateVars={columnsRequired}
          categories={[]}
          checkImage={checkFileIsImg}
        />
      ),
    });
  };

  const redirectToKpiCreation = () => {
    history.push(`/kpi/${urlHash(`${dashboardKey}/new`)}`);
  };

  const redirectToInsightCreation = () => {
    history.push(`/label/${urlHash(`${dashboardKeyValid}/new`)}`);
  };

  useEffect(() => {
    if (showToPrint) {
      setTimeout(() => {
        setManualLoad(false);
        onBeforeGetContentResolve.current();
      }, 5000);
    }
  }, [showToPrint, onBeforeGetContentResolve]);

  const handleOnBeforeGetContent = () => {
    setManualLoad(true);
    return new Promise((resolve) => {
      onBeforeGetContentResolve.current = resolve;
      setTimeout(() => {
        setShowToPrint(true);
      }, 1000);
    });
  };

  const reactToPrintContent = React.useCallback(() => (
    gridKpiRef.current
  ), [gridKpiRef.current]);

  const handlePrint = useReactToPrint({
    content: reactToPrintContent,
    onBeforeGetContent: handleOnBeforeGetContent,
    onAfterPrint: () => setShowToPrint(false),
  });


  const handleFullScreen = () => {
    handle.enter();
  };

  const getUserName = (userId) => {
    return (usersState?.users?.find((u) => u.id === userId)?.name || '');
  }

  /**
   * @async Fetch the values of the control variable
   * @param {object} database Database file id
   * @param {string} control Variable name
   * @param {string} dateType Date selector (only for date variable)
   * @returns {array} Value array options
   */
  const getControlValues = async (database, control, dateType) => {
    const file = dashFiles.find((df) => df.file_id === database);
    if (!file) {
      return { fileError: [
        'Arquivo necessário não encontrado',
        'O arquivo responsável pela geração deste KPI foi excluído. Desta forma, não é possivel utilizar esta ferramenta.',
      ] };
    } else if (!file.allowed) {
      return { fileError: [
        'Permissões insuficientes',
        `Você não possui permissão de leitura para o arquivo "${file.filename}".`,
        `Para utilizar esta ferramenta, solicite as permissões necessárias ao dono do arquivo: ${getUserName(file.owner)}`
      ] };
    }
    const { res, msg, error: er } = await filesAPI.getUnique(database, control, dateType);
    if (er) {
      setAlertConfig({ type: 'error', text: error.generic, child: msg });
      return { opts: [] };
    } if (res) {
      return { opts: res.map((val) => ({ label: val, value: val, id: val })) };
    }
    return { opts: [] };
  };

  /** Request temporary kpi data */
  const handleDynamicKpi = (body, kpiFile) => (
    kpiDataAPI.genKpiData(body, kpiFile)
  );

  const handleShowToolbar = () => {
    setShowToolbar(!showToolbar);
  };

  const getUsersShareds = () => {
    try {
      if (dashboardDoc) {
        const sharedIds = Object.keys(dashboardDoc.sharedWith || {});
        sharedIds.push(dashboardDoc.owner);
        const usersShareds = usersState.users.filter((u) => sharedIds.includes(u.id));
        return usersShareds;
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  const handleArchiveComment = (commentId) => {
    setAlertConfig({
      type: 'warning',
      text: 'Tem certeza que deseja remover este comentário?',
      withoutConfirm: true,
      withFunction: true,
      confirmFunction: () => gridKpiAPI.archiveComment(commentId),
    });
  };

  const handleDependencies = () => {
    // const dashFiles = dashboardDoc.databases
    // ? filesState.files.filter((f) => dashboardDoc.databases.includes(f.file_id))
    // : [];

    setModalConfig({
      title: 'Lista de dependências',
      yesLabel: 'Ok',
      children: (
        <DashboardDependencies
          currentUser={user}
          dashboard={dashboardDoc}
          files={dashFiles}
          users={usersState.users}
          numberOfKpis={kpiItemList.length}
          close={closeModal}
          deviceType={device}
        />
      ),
    });
  };

  const getFileColumns = async (database) => {
    let opts = [];
    const res = await filesAPI.getColumns(database);
    if (res.error) {
      setAlertConfig({ type: 'error', text: error.generic, child: res.msg });
      return opts;
    } if (res.columns) {
      setDashFiles((files) => files.map((f) => {
        if (f.file_id === database) {
          opts = Object.keys(res.columns).map((val) => ({
            label: val, value: val, id: val, type: getType(res.columns[val]),
          }));
          f.columns = opts;
        } return f;
      }));
      return opts;
    }
    return opts;
  };

  const handleGlobalFilterSelectors = async (globalFilters) => {
    const result = await gridKpiAPI.setGlobalFilterSelectors(globalFilters);
    if (result.error) {
      setAlertConfig({
        type: 'error',
        text: result.msg,
        child: `${result.raw}`,
      });
      return;
    }
    setAlertConfig({
      type: 'success',
      text: 'Colunas de filtro global editadas com sucesso!',
    });
  };

  const filterColumnsModal = (toolbarStateSetter) => {
    setModalConfig({
      title: 'Filtro global',
      children: (
        <GlobalFiltersColumns
          usedFiles={dashFiles}
          getFileColumns={getFileColumns}
          hide={closeModal}
          globalFiltersState={[globalFilterSelectors, setGlobalFilterSelectors]}
          showToolbar={toolbarStateSetter}
          submit={handleGlobalFilterSelectors}
        />
      ),
    });
  };

  const checkAccessToDependency = (kpi) => {
    const dependency = dashFiles.find((file) => file.file_id === kpi.database);
    return dependency;
  }

  const getKpiWithoutDependencyMessage = (dependency, kpi) => {
    const originalFile = filesState.instanceFiles.find((file) => file.file_id === kpi.database);
    if (!originalFile) {
      setAlertConfig({
        type: 'error',
        text: 'Dependência não existe!',
        child: (
          <div style={{ fontSize: '14px' }}>
            O arquivo utilizado na construção deste KPI foi apagado. Portanto, este KPI não pode ser atualizado!
          </div>
        ),
      });
      return;
    }
    const currentOwner = usersState.users.find((u) => u.uid === dependency.owner);
    setAlertConfig({
      type: 'warning',
      text: 'Você não possui acesso ao arquivo de origem!',
      child: (
        <>
          <div style={{ fontSize: '14px' }}>
            Você não possui permissão de acesso ao arquivo
            {' '}
            <b style={{ color: theme.warning }}>{dependency.filename}</b>
            {' '}
            de propriedade do(a) usuário(a)
            {' '}
            <b style={{ color: theme.warning }}>{currentOwner.name || currentOwner.email.split('@')[0]}</b>
            .
          </div>
          <div style={{ fontSize: '14px' }}>
            Para atualizar este KPI, solicite o compartilhamento do arquivo
            {' '}
            <b style={{ color: theme.warning }}>{dependency.filename}</b>
            {' '}
            ao proprietário.
          </div>
        </>
      ),
    });
  };

  return (
    <div>
      {(isLoading || gridAPILoading || manualLoad || filesState.isLoading) && (
        <Loader underText={manualLoad ? printLoadMsg : ''} />
      )}
      <GridKpisToolbar
        isOwner={isOwner}
        isAdmin={claimsUser?.is_admin}
        isGuest={user.role === 'guest'}
        dashboardKeyValid={dashboardKeyValid}
        gridKpiState={gridKpiState}
        gridKpiAPI={gridKpiAPI}
        editPainel={editPainel}
        setEditPainel={setEditPainel}
        goBack={() => history.push('/home')}
        openMural={() => setOpenMural(true)}
        openSaveAsTemplate={handleCreateTemplate}

        // openShare={() => setOpenShare(true)}
        openShare={handleShare}
        // snaps
        handleCreateSnapshot={handleCreateSnapshot}
        handleEditSnapshot={handleEditSnapshot}
        handleDeleteSnapshot={handleDeleteSnapshot}
        changeCurrentSnap={changeCurrentSnap}
        //
        redirectToInsightCreation={redirectToInsightCreation}
        redirectToKpiCreation={redirectToKpiCreation}
        // card Styles
        handleCardStyles={handleCardStyles}
        //
        printFunction={handlePrint}
        gridBreakpoint={gridBreakpoint}
        showToolbar={showToolbar}
        handleShowToolbar={handleShowToolbar}
        getDependencies={handleDependencies}
        handleFullScreen={handleFullScreen}
        // Global Filters
        fileDependencies={dashFiles}
        globalFilters={globalFilters}
        globalFilterSelectors={globalFilterSelectors}
        setGlobalFilterSelectors={setGlobalFilterSelectors}
        getColumnValues={getControlValues}
        filterColumnsModal={filterColumnsModal}
      />
      {kpiItemList.length > 0 ? (
        <GridKpis
          gridKpiRef={gridKpiRef}
          isOwner={isOwner}
          isSuper={user.role === 'super'}
          dashboardFileDependencies={dashFiles}
          editPainel={editPainel}
          showToPrint={showToPrint}
          dashboardKey={dashboardKeyValid}
          saveLayout={handleSaveLayout}
        //
          kpiItemList={kpiItemList}
          dashboardDoc={dashboardDoc}
          kpisLayout={kpisLayout}
        //
          gridKpiAPI={gridKpiAPI}
          gridKpiState={gridKpiState}
          gridBreakpoint={gridBreakpoint}
          setGridBreakpoint={setGridBreakpoint}
          showToolbar={showToolbar}
        //
          getControlValues={getControlValues}
          handleDynamicKpi={handleDynamicKpi}
          checkAccessToDependency={checkAccessToDependency}
          getKpiWithoutDependencyMessage={getKpiWithoutDependencyMessage}
          fullScreenHandler={handle}
        //
          globalFilters={globalFilters}
        />
      ) : !isLoading && (
        <NoKpi
          dashboardKey={dashboardKeyValid}
          isShared={userId !== currentUser.uid}
          role={user.role}
        />
      )}
      <Mural
        open={openMural}
        close={closeMural}
        users={getUsersShareds()}
        currentUser={currentUser}
        kpiState={gridKpiState}
        // loaders
        loadComments={gridKpiAPI.loadComments}
        loadMoreComments={gridKpiAPI.loadMoreComments}
        // actions
        addComment={gridKpiAPI.addComment}
        editComment={gridKpiAPI.editComment}
        archiveComment={handleArchiveComment}
      />
    </div>
  );
}
